import { Link } from 'react-router-dom';
import { Package, Box } from 'lucide-react';
import type { ProductWithMedia } from '../../types/products';
import { PRODUCT_TYPE_LABELS } from '../../types/products';
import { formatPrice } from '../../services/productService';

interface ProductListViewProps {
  products: ProductWithMedia[];
  isLoading?: boolean;
}

export function ProductListView({ products, isLoading }: ProductListViewProps) {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-100 dark:border-slate-600">
              <tr>
                <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3">Bild</th>
                <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3">Name</th>
                <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Art.-Nr.</th>
                <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3 hidden md:table-cell">Kategorie</th>
                <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Typ</th>
                <th className="text-right text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3">Preis</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {[...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-4 py-3"><div className="w-12 h-12 bg-gray-200 dark:bg-slate-600 rounded-lg" /></td>
                  <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-32" /></td>
                  <td className="px-4 py-3 hidden sm:table-cell"><div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-16" /></td>
                  <td className="px-4 py-3 hidden md:table-cell"><div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-24" /></td>
                  <td className="px-4 py-3 hidden lg:table-cell"><div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-16" /></td>
                  <td className="px-4 py-3 text-right"><div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-16 ml-auto" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-12 text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Package className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Keine Produkte gefunden
        </h3>
        <p className="text-gray-500 dark:text-slate-400">
          Versuche andere Suchbegriffe oder Filter.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-100 dark:border-slate-600">
            <tr>
              <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3 w-16">Bild</th>
              <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3">Name</th>
              <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Art.-Nr.</th>
              <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3 hidden md:table-cell">Kategorie</th>
              <th className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Typ</th>
              <th className="text-right text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider px-4 py-3">Preis</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {products.map((product) => {
              const primaryImage = product.media.find((m) => m.type === 'image');

              return (
                <tr
                  key={product.id}
                  className="hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors cursor-pointer group"
                  onClick={() => window.location.href = `/products/${product.slug}`}
                >
                  {/* Bild */}
                  <td className="px-4 py-3">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-slate-700 rounded-lg overflow-hidden flex-shrink-0">
                      {primaryImage ? (
                        <img
                          src={primaryImage.url}
                          alt={primaryImage.alt || product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-6 h-6 text-gray-300" />
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Name */}
                  <td className="px-4 py-3">
                    <Link
                      to={`/products/${product.slug}`}
                      className="font-medium text-[#1A1A1A] dark:text-white group-hover:text-primary transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {product.name}
                    </Link>
                  </td>

                  {/* Art.-Nr. */}
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-sm text-[#6B6B6B] dark:text-slate-400">{product.sku}</span>
                  </td>

                  {/* Kategorie */}
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-sm text-[#6B6B6B] dark:text-slate-400">
                      {product.primary_category || '-'}
                    </span>
                  </td>

                  {/* Typ */}
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {product.product_type !== 'single' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                        <Box className="w-3 h-3" />
                        {PRODUCT_TYPE_LABELS[product.product_type]}
                      </span>
                    ) : (
                      <span className="text-sm text-[#6B6B6B] dark:text-slate-400">-</span>
                    )}
                  </td>

                  {/* Preis */}
                  <td className="px-4 py-3 text-right">
                    <span className="font-semibold text-[#1A1A1A] dark:text-white">
                      {formatPrice(product.price_gross, product.currency)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
